/**
 * Light, dark, or follow the device. In the design system.
 *
 * It was a Tailwind popover with two palettes stacked on top of each other,
 * a light one corrected class by class for the dark theme, a hand-tuned
 * navy for its dark ground, and a focus ring the rest of the system does
 * not draw. It sits inside a `.ds` scope everywhere it renders
 * (the app bar and the public masthead both carry the scope by hand), so it
 * is the system's ghost icon button and the system's menu, and both themes
 * come out of the same tokens.
 *
 * The three choices are the app's, from ThemeContext, not the kit's own
 * two-way switch: "System" is a real option here and the kit's `ThemeSwitch`
 * does not have it.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, Monitor, Moon, Sun } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { Button, Menu, MenuItem } from '@/design-system/primitives'
import '@/design-system/components.css'

const THEMES = [
  { value: 'light' as const, label: 'Light', icon: Sun },
  { value: 'dark' as const, label: 'Dark', icon: Moon },
  { value: 'system' as const, label: 'System', icon: Monitor },
]

export default function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const hangRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const onDown = (event: MouseEvent) => {
      if (hangRef.current && !hangRef.current.contains(event.target as Node)) setIsOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen])

  const current = THEMES.find((t) => t.value === theme) ?? THEMES[0]
  const CurrentIcon = current.icon

  return (
    <span className="ds-menuhang" ref={hangRef}>
      <Button
        variant="ghost"
        size="sm"
        icon
        aria-label="Toggle theme"
        title={`Theme: ${current.label}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <CurrentIcon aria-hidden="true" />
      </Button>

      {isOpen && (
        <Menu aria-label="Theme">
          {THEMES.map(({ value, label, icon: Icon }) => (
            <MenuItem
              key={value}
              icon={<Icon aria-hidden="true" />}
              /*
                One choice is in force at a time, so these are radios. On a
                plain `menuitem` the `aria-checked` below is not valid ARIA
                and a screen reader announces none of them as selected, which
                is the same thing the tick says to everybody else.
              */
              role="menuitemradio"
              aria-checked={theme === value}
              trailing={theme === value ? <Check className="ds-menuhang__now" aria-hidden="true" /> : undefined}
              onClick={() => {
                setTheme(value)
                setIsOpen(false)
              }}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </span>
  )
}
